// Draft a runtime lens spec from a judged candidate.
//
// This is where the two halves of the loop meet: the Behaviours tab ranks
// candidates and names them, and this turns the winner into something mountable
// without a deploy. It is a DRAFT and says so, for a reason worth stating plainly.
//
// The judge's verdict names the FIELDS worth extracting — `namespace`, `outcome`,
// `target` — because naming them is interpretive work only a reader of the raw
// commands can do. It does not say WHERE those values live, and it cannot: the path
// is a fact about one harness's argument names, not about the behaviour. So a draft
// binds what it can derive honestly — the statement, the program — carries the
// judge's field names forward as `wanted` so nothing is lost, and leaves the paths
// to whoever refines it.
//
// Drafting a full field map by guessing paths from field names is the mechanical-
// proxy-for-meaning failure in its purest form: `namespace` → `flag:-n` is right
// for kubectl and wrong for helm, and wrong SILENTLY, producing a lens whose rows
// are missing a column that looks like the tool not carrying it.

import { splitStatements, tokenize, resolveShellVars } from "../../xray-factory/src/mine/shell.js";
import { skeletonizeShell } from "../../xray-factory/src/mine/skeleton.js";
import { discriminatorSupport, probeSupport } from "../../xray-factory/src/judge/index.js";

const MAX_LABEL = 12;
const MIN_TITLE = 30;

// Shell housekeeping, and the wrappers a program hides behind. Mirrors what
// `shellStatements` skips when it resolves a program, because a draft that matched
// `cd` would claim every compound command in the corpus.
const SKIP = /^(?:cd|pushd|popd|mkdir|export|set|unset|source|\.|echo|printf|true|false|which|command|type|exit|sleep|wait|eval|sudo|env|nohup|time|exec|builtin)$/i;

export function kebabId(text, fallback = "lens") {
  const k = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24)
    .replace(/-+$/, "");
  // The contract wants 2-24 lowercase kebab; a name that reduces to nothing (or to
  // one character) gets the fallback rather than an id the compiler will refuse
  // with a message about a name the author never typed.
  return k.length >= 2 ? k : fallback;
}

// The tab-strip label, cut at a word boundary. A blind slice shipped labels like
// "Conflict mar" and "Background t", which read as rendering bugs on every
// surface that shows them; whole words that fit say less and read as chosen.
// Hyphens are boundaries too — "Patch-and-verify cycle" fits as "Patch-and",
// not as "Patch-and-ve".
function labelFrom(name, fallback = "Behaviour") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean)
    .flatMap((w, i) => w.split("-").map((seg, j) => ({ seg, sep: j > 0 ? "-" : i > 0 ? " " : "" })))
    .filter((p) => p.seg);
  if (!parts.length) return fallback;
  let label = parts[0].seg.slice(0, MAX_LABEL);
  for (const { seg, sep } of parts.slice(1)) {
    if (label.length + sep.length + seg.length > MAX_LABEL) break;
    label += sep + seg;
  }
  return label;
}

/**
 * The distinct programs a cluster's instances actually ran.
 *
 * Read from the raw commands through the same grammar the extractor will use, not
 * from the skeleton string: the skeleton has already lost which token was the
 * program once wrappers and assignments are in play.
 */
export function programsOfCluster(cluster) {
  const seen = new Map();
  for (const inst of cluster?.instances || []) {
    for (const st of splitStatements(resolveShellVars(String(inst?.command || "")))) {
      const argv = tokenize(st.text.trim()).filter(Boolean);
      let i = 0;
      while (i < argv.length) {
        const t = argv[i];
        if (/^\$?\w+=/.test(t)) { i += 1; continue; }
        if (SKIP.test(t)) { i += /^(?:cd|pushd|source|\.)$/i.test(t) ? 2 : 1; continue; }
        break;
      }
      const raw = argv[i];
      if (!raw || !/^[A-Za-z_$@./\\][\w.@:+-]*$/.test(raw)) continue;
      const prog = raw.split(/[\\/]/).pop().replace(/\.exe$/i, "").toLowerCase();
      if (!prog || SKIP.test(prog)) continue;
      seen.set(prog, (seen.get(prog) || 0) + 1);
    }
  }
  // Commonest first: a cluster's skeleton is one shape, so the head is the program
  // that shape is about and the tail is whatever shared a pipeline with it.
  return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([p]) => p);
}

/**
 * A mountable spec drafted from a verdict plus the cluster it judged.
 *
 * @returns {{spec: object|null, wanted: string[], notes: string[]}}
 *   `notes` are what a reader must know before trusting it — surfaced in the UI
 *   rather than left for them to discover from an empty column.
 */
export function draftSpecFromVerdict(verdict, cluster, { takenIds = [] } = {}) {
  const notes = [];
  const wanted = Array.isArray(verdict?.fields) ? verdict.fields.filter((f) => typeof f === "string") : [];
  const programs = programsOfCluster(cluster).slice(0, 6);
  const skeleton = String(cluster?.skeleton || "").trim();

  if (!skeleton) {
    return {
      spec: null,
      wanted,
      notes: ["This cluster carries no skeleton, so there is no shape to match on."],
    };
  }
  if (!programs.length) {
    // No program means no door, and a spec with no door claims nothing. Better to
    // refuse than to mount a lens that reads as "found nothing".
    return {
      spec: null,
      wanted,
      notes: ["No shell program could be resolved from this cluster's commands, so there is no door to match on."],
    };
  }

  // The discriminator the judge's claim rests on. A subset claim WITHOUT one
  // must not draft: the lens's door would be the whole skeleton, stamping the
  // subset's name on every call that merely shares the shape — the exact
  // over-claim the claimScope contract exists to stop.
  const where = typeof verdict?.where === "string" && verdict.where.trim() ? verdict.where.trim() : null;
  if (verdict?.claimScope === "subset" && !where) {
    return {
      spec: null,
      wanted,
      notes: [
        "This verdict claims a subset of the cluster but carries no `where` discriminator, so a lens"
        + " drafted from it would claim the whole shape under the subset's name. Re-judge rather than mounting it.",
      ],
    };
  }
  const outcomePattern = typeof verdict?.outcomePattern === "string" && verdict.outcomePattern.trim()
    && probeSupport(cluster, { on: "output", pattern: verdict.outcomePattern }).re
    ? verdict.outcomePattern.trim()
    : null;
  const support = where ? discriminatorSupport(cluster, where) : null;
  if (where && !support.re) {
    return {
      spec: null,
      wanted,
      notes: ["This verdict's `where` discriminator is not a valid pattern, so the claimed subset cannot be carved. Re-judge rather than mounting it."],
    };
  }

  // The verdict's proof obligations, carried into the lens so they keep
  // executing after the judgement. Malformed ones are dropped WITH a note —
  // silently thinning the probe list would make the lens claim more than the
  // verdict did, in the quietest possible way. "Malformed" includes a pattern
  // that does not COMPILE in the lens's engine: a judge stress-testing in
  // another regex dialect can return probes that measured perfectly there and
  // throw here, and carrying one forward just moves the refusal to mount time,
  // where the reader can do nothing about it.
  const rawChecks = Array.isArray(verdict?.checks) ? verdict.checks : [];
  const checks = rawChecks
    .filter((c) => c && typeof c === "object"
      && String(c.name || "").trim()
      && (c.on === "command" || c.on === "output")
      && typeof c.pattern === "string" && c.pattern.trim()
      && probeSupport(cluster, c).re !== null)
    .map((c) => ({ name: String(c.name).trim().slice(0, 48), on: c.on, pattern: c.pattern }));
  const droppedChecks = rawChecks.length - checks.length;

  // Every exact shape this cluster was OBSERVED wearing — the survivor the
  // miner kept plus the near-duplicate cores it absorbed. Capped at the same
  // ceiling the compiler enforces; past it the cluster is a family and the
  // honest fix is remining, not a wider door.
  const observed = new Set([skeleton]);
  for (const inst of cluster?.instances || []) {
    const core = skeletonizeShell(String(inst?.command || "")).core;
    if (core) observed.add(core);
    if (observed.size >= 12) break;
  }
  const observedShapes = [...observed];

  // Which positional the reading groups by. `arg:last` is the old default and
  // is wrong wherever the command's tail is a flag value or a subcommand word;
  // on a real corpus it grouped a lint lens under "clippy" and a test lens
  // under a test-name filter. When most of the cluster's commands carry a
  // path-shaped positional, the path is what the rows are about.
  const withPath = (cluster?.instances || []).filter((inst) => /(?:^|\s)[\w.@:+-]*[\\/][\w./@:+-]+|\s[\w-]+\.[A-Za-z0-9]{1,6}(?:\s|$)/.test(String(inst?.command || ""))).length;
  const targetSource = withPath * 2 >= (cluster?.instances || []).length && withPath > 0 ? "arg:path" : "arg:last";

  const base = kebabId(verdict?.name, "behaviour");
  let id = base;
  const taken = new Set(takenIds);
  for (let n = 2; taken.has(id); n += 1) id = `${base.slice(0, 22)}-${n}`.slice(0, 24);

  const label = labelFrom(verdict?.name);
  const summary = String(verdict?.summary || "").trim();
  const target = String(verdict?.target || "").trim();
  // The contract wants >=30 chars because the tooltip is the only place a reader
  // learns what a tab covers. Compose rather than pad: summary, then the target it
  // acts on, then the programs it watches.
  let title = summary;
  if (target && title.length < MIN_TITLE * 2) title += `${title.endsWith(".") ? "" : "."} Target: ${target}`;
  if (title.length < MIN_TITLE) title += ` Matched on ${programs.join(", ")}.`;
  title = title.trim();
  if (title.length < MIN_TITLE) {
    // A verdict with no summary and no target leaves nothing to compose from, and the
    // program list alone can be short enough to miss the floor — two programs named
    // `jq` and `sed` get you 22 characters. Padding it would produce a tooltip that
    // says nothing; drafting it anyway produced a spec that could not compile, so
    // Mount failed on a title the reader had no way to fix from the panel.
    return {
      spec: null,
      wanted,
      notes: [
        "This verdict carries no summary and no target, so there is nothing to say in the"
        + " tooltip that a reader could act on. Re-judge the cluster rather than mounting it.",
      ],
    };
  }

  const traceOnly = verdict?.axis !== "scope-tab";
  const spec = {
    id,
    label,
    title,
    consumer: verdict?.consumer || "",
    decision: verdict?.decision || "",
    // Match the clustered SHAPES, not the programs. A pipeline declared as its
    // programs becomes a disjunction — "read filtered test output" drafted as
    // [npx, grep, tail] matched 91 unrelated greps — and the statement door would
    // also split each pipeline into one op per stage. The shapes are exact AND
    // complete: the miner folds near-duplicates into a surviving skeleton, so a
    // door that tested only the survivor dropped the absorbed variants — a 20×
    // cluster mounted as a lens that matched eight. Every distinct core observed
    // in the kept instances is a door. `where` narrows further to the calls the
    // judge's claim was actually about, when the claim was a subset.
    match: {
      shell: {
        // An OUTCOME-defined behaviour doors on the result and deliberately
        // drops the shape door: its whole point is that it spans shapes, and
        // keeping the originating cluster's skeleton would pin it back to the
        // one fragment the judge happened to attach it to.
        ...(outcomePattern
          ? { outputPattern: outcomePattern }
          : observedShapes.length > 1 ? { skeletons: observedShapes } : { skeleton }),
        ...(where ? { where } : {}),
      },
    },
    ...(checks.length ? { checks } : {}),
    // What a draft can bind without guessing a path. `target` is the last
    // positional — the thing most shell commands act on — and it is what makes the
    // reading discriminating: grouping by PROGRAM put all 56 runs of a one-program
    // cluster in a single row, which tells a reader nothing. `command` stays bound
    // as the evidence, so a wrong target is visible rather than just empty.
    // `output` was missing and it is the most valuable field a shell op has: it is
    // what came back, and without it every row said "answered" and showed nothing.
    // Binding it also makes the sighting comparison possible — two runs of the same
    // target whose output DIFFERS is the finding, and the one a reader acts on.
    //
    // The transport tri-state is named `call`, never `outcome`: it says whether the
    // CALL answered, and on a lens whose title promises a domain outcome ("did the
    // background task succeed"), a row reading "OUTCOME answered" over a build
    // still mid-flight claims the thing the lens has not yet read. The task-level
    // outcome, when it exists, is a refine-located field over the output.
    fields: {
      target: targetSource, program: "program", command: "command",
      output: "output", call: "outcome",
    },
    // Always group by the target. With a skeleton door every op is the SAME shape,
    // so the program is constant across the group and grouping by it yields exactly
    // one row — which is what made the first mounted lenses useless.
    group: { by: "target", label: "{target}" },
    // A STREAM, declared rather than defaulted: a shell skeleton has one identity
    // and no natural container, and dressing that up as a hierarchy would invent a
    // level. Refine can add `group.within` once it has read the instances and found
    // a second identity worth nesting under.
    shape: "stream",
    // Track the output across runs of the same target. "ran 3× · changed between
    // reads" on a test suite means the failures moved, which is the whole question a
    // reader of that lens has.
    sighting: { compare: "output" },
    result: { as: "text", from: "output" },
    ...(traceOnly
      ? { tab: false, tabReason: verdict?.axisReason || "judged trace-only" }
      : { substrate: { label } }),
    feed: { domain: id },
    lineageReason: "a drafted lens does not claim provenance edges; promote it first",
    // The full name, not the label: the note is a sentence, and truncating the
    // behaviour's name inside it reads as a rendering bug.
    emptyNote: `No ${String(verdict?.name || "").trim() || label} work in this window`,
    // Provenance for the mount, so a reader of the stored spec knows it was drafted
    // from a verdict rather than hand-written, and against which candidate.
    draftedFrom: {
      verdictKey: verdict?.key || null,
      falsifier: verdict?.falsifier || null,
      claimScope: verdict?.claimScope || null,
      where,
      checks,
      wanted,
    },
  };

  if (outcomePattern) {
    notes.push(
      `Doors on the RESULT — /${outcomePattern}/ — not on a command shape, because the behaviour`
      + " spans shapes. It claims any shell call in a trace whose output matches, and no call whose"
      + " result the trace never captured.",
    );
  }
  notes.push(
    (outcomePattern
      ? `Grouped by target (${targetSource === "arg:path" ? "the last path-shaped positional" : "the head stage's last positional"})`
      : observedShapes.length > 1
      ? `Matches the ${observedShapes.length} exact shapes observed in this cluster (the miner folds near-duplicates, and a door on only the survivor under-claims), grouped by target`
      : `Matches exactly \`${skeleton}\`, grouped by target`)
    + ` (${targetSource === "arg:path" ? "the last path-shaped positional" : "the head stage's last positional"} — a guess at what the command acts on)`
    + ". The judge asked for "
    + (wanted.length ? `${wanted.join(", ")}` : "no specific fields")
    + " — those need field paths, which depend on the harness's argument names and cannot be guessed from the "
    + "names alone. Refine locates them by reading the raw instances.",
  );
  if (where) {
    // The measured split is the honest version of the claim: a reader mounting
    // this sees what fraction of the shape the name actually covers, instead of
    // discovering it as a thinner-than-expected reading.
    notes.push(
      `Claims only commands matching /${where}/ — ${support.matched} of ${support.total} sampled instances.`
      + " The rest of the shape stays unclaimed rather than being stamped with this name.",
    );
  }
  if (checks.length) {
    // Each probe's measured support, so the mount decision is made against the
    // same numbers the lens will keep producing per-op.
    const measured = checks
      .map((c) => {
        const cs = probeSupport(cluster, c);
        return `${c.name} (${c.on}) ${cs.matched}/${cs.total}`;
      })
      .join(" · ");
    notes.push(
      `Probes carried into the lens, re-checked on every claimed call — sampled support: ${measured}.`
      + " A probe failing on live work renders as ✗ on the op, which is the falsifier firing in view.",
    );
  }
  if (droppedChecks) {
    notes.push(`${droppedChecks} malformed check${droppedChecks === 1 ? " was" : "s were"} dropped from the verdict — the lens proves less than the judge claimed.`);
  }
  if (!traceOnly) {
    notes.push("Judged scope-tab, so it also claims a tab and a scan window. Check the window is not over-broad before trusting the tab.");
  }
  if ((cluster?.sessions ?? 0) <= 1) {
    notes.push("This candidate was confined to one session, so there is no cross-session pattern for it to aggregate yet.");
  }
  return { spec, wanted, notes };
}
